import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  ARRIVAL_FADE_CLASS,
  STAGGER_CLASS,
  staggerStyle,
  useInitialIds,
  useNewIds,
} from '@/shared/motion/arrival';

import { FeatureErrorBoundary } from '@/shared/components/FeatureErrorBoundary';
import { PageHeader } from '@/shared/components/PageHeader';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { useWorkspace } from '@/features/workspaces';
import { useCanWork } from '@/features/workspaces/canWork';
import {
  CapacityMeter,
  WorkspaceConcurrencyProvider,
} from '@/features/concurrency';
import { useIsOrgAdmin } from '@/features/identity';
import {
  LIVE_POLL_MS,
  READER_WAITING_LABEL,
  TaskDrawer,
  hasNonTerminalActivity,
  useArrivalHold,
  useHoldZone,
  useInteractionHold,
  useWorkspaceQueue,
  taskPlacement,
  type TaskTab,
  type WorkspaceTaskRow,
} from '@/features/tasks';
import {
  RelatedTaskHostProvider,
  tasksPageHost,
} from '@/features/task-relations';

import { useTaskListRows, type TaskListRows } from '../useTaskListRows';
import { clearFilters, searchOfListView, type TaskListView } from '../listView';
import { summarizeTaskTiles, workflowLeadMedians } from '../taskTiles';
import { useListNavigation } from '../useListNavigation';
import { useCommandPaletteStore } from '@/shared/store/commandPalette';
import { TaskListEmptyState } from './TaskListEmptyState';
import { LiveStatus } from '@/shared/components/LiveStatus';
import { TaskListChips } from './TaskListChips';
import { TaskListFilters } from './TaskListFilters';
import { TaskTiles, TaskTilesSkeleton } from './TaskTiles';
import { TaskRow } from './TaskRow';
import { TaskRowActions } from './TaskRowActions';
import { taskRowHeight } from '../rowHeight';
import {
  usePreferencesStore,
  type TaskListDensity,
} from '@/shared/store/preferences';
import { TaskListHeader, TASK_LIST_HEADER_HEIGHT } from './TaskListHeader';
import { TASK_GRID_CLASS, TASK_GRID_STYLE, useTaskTable } from '../taskColumns';
import { ReadFailure } from '@/shared/components/ReadFailure';
import { NewTaskMenu } from './NewTaskMenu';

/**
 * Rows that are not there yet, at the height the real ones will be.
 *
 * FR-021: "the page MUST reserve row space while loading so that rows do not
 * shift when data arrives". A generic pulse block satisfies the letter of
 * "something is loading" and none of the intent — the list still jumped, because
 * the filter bar above it did not exist until the data did.
 *
 * Measured against the same exported constant the virtualiser uses, so the
 * placeholder and the real list cannot drift apart.
 */
function SkeletonRows({
  count = 8,
  rowHeight,
}: {
  count?: number;
  rowHeight: number;
}) {
  return (
    // The status role, live region, busy state and named sentence were written
    // out here by hand, correctly, and then differently again on two other
    // pages. `PendingRegion` is that contract in one place.
    <PendingRegion
      label="Loading tasks"
      className="border-border min-h-0 flex-1 overflow-hidden rounded-lg border"
    >
      {/* The heading row's footprint too, or the first row would land 32px
          lower than its placeholder did. */}
      <div
        className="border-border border-b"
        style={{ height: TASK_LIST_HEADER_HEIGHT }}
      />
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          style={{ ...TASK_GRID_STYLE, height: rowHeight }}
          className={`${TASK_GRID_CLASS} border-border/60 items-center border-b`}
        >
          <div className="flex min-w-0 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-48 max-w-full rounded" />
            <Skeleton className="h-3 w-32 max-w-full rounded" />
          </div>
          <Skeleton className="h-3 w-20 rounded" />
          <Skeleton className="h-3 w-16 rounded max-md:hidden" />
          <Skeleton className="h-3 w-16 rounded max-lg:hidden" />
          <Skeleton className="ml-auto h-3 w-10 rounded max-lg:hidden" />
          <Skeleton className="ml-auto h-3 w-12 rounded max-sm:hidden" />
          <Skeleton className="ml-auto h-3 w-12 rounded max-sm:hidden" />
          <Skeleton className="ml-auto h-3 w-10 rounded" />
        </div>
      ))}
    </PendingRegion>
  );
}

export interface WorkspaceTasksPageProps {
  workspaceId: string;
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
  /** The task whose drawer is open over the list, from the address (spec 111). */
  openTaskId?: string;
  openTab?: TaskTab;
  onTabChange: (tab: TaskTab, options?: { replace?: boolean }) => void;
  onCloseTask: (options?: { replace?: boolean }) => void;
  /** The file shown beside the open task, from the address; the drawer's. */
  previewPath?: string;
  /** Takes `preview` out of the address; required, as the drawer's is. */
  onPreviewClose: () => void;
}

/**
 * Every task in the workspace, in one flat list.
 *
 * A board is exactly one workflow, which is the limitation this page removes.
 * Nothing here edits anything: every row is a way *into* the board that owns the
 * task, and a second place to change a task is a second place it can be changed
 * inconsistently (FR-023/024).
 *
 * **What this page costs, stated rather than hidden.** The whole aggregate is
 * fetched regardless of any filter, and every filter is computed client-side.
 * Virtualising bounds what is *drawn*, not what is *fetched* — the same trade
 * `066-workspace-overview` accepted knowingly and recorded ([D3]). If a
 * workspace ever grows past what one aggregate should carry, that is a contract
 * problem and no amount of work on this page fixes it.
 *
 * **Since spec 110** the page opens with summary tiles counted over the whole
 * workspace, the list is a table whose columns are defined once in
 * `taskColumns.tsx` (superseding 068 FR-004's row fields), and the page
 * re-reads the aggregate on the board's cadence while — and only while —
 * something is in flight. Order and filtering are unchanged: the table sorts
 * and filters nothing.
 *
 * **Since spec 112** the list is described by a `TaskListView` — several
 * states, workflows, people, a time range, hidden tasks and an order — read
 * from and written to the address; the table still owns no state, and
 * `orderTaskRows` does the ordering.
 *
 * **Since spec 113** the page says what it shows: the active filters as
 * removable chips, the count at the end of the filter row, all five tiles at
 * every count, tinted states where a person is needed, a mark on tasks well past
 * their workflow's usual time, and whether the page is live. The rows are
 * derived once, by `useTaskListRows`, for everything here that reads them.
 *
 * **Since spec 114** the page can be worked as well as read: All / Mine /
 * Needs me set the filters in one press, rows are cozy or compact (a device
 * preference), and each row offers Open on board, Copy link and Hide beside
 * its link. Hiding from a row is the one change this page makes to a task —
 * spec 068 FR-023, narrowed.
 */
/** A list row's task id, for the arrival hold. */
const rowTaskId = (row: WorkspaceTaskRow) => row.taskId;

/** The list as one zone arrivals wait outside of (spec 148 FR-002). */
const LIST_ZONE = 'list';

export function WorkspaceTasksPage({
  workspaceId,
  view,
  onViewChange,
  openTaskId,
  openTab,
  onTabChange,
  onCloseTask,
  previewPath,
  onPreviewClose,
}: WorkspaceTasksPageProps) {
  const drawerOpen = openTaskId !== undefined;

  // The narrow window's Filters panel (spec 113 FR-014). Closed when a task
  // opens — adjusted during render, React's pattern for state that follows a
  // prop, so the panel never paints over the drawer for a frame.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [panelTask, setPanelTask] = useState(openTaskId);
  if (panelTask !== openTaskId) {
    setPanelTask(openTaskId);
    if (openTaskId !== undefined) setSheetOpen(false);
  }

  // Hidden tasks come from the read that includes them — the board's "show
  // hidden" read — and only while the view asks for them (spec 112 US5).
  const {
    data: workspace,
    isPending,
    dataUpdatedAt,
  } = useWorkspace(workspaceId, {
    includeHidden: view.hidden,
    keepWhileSwitchingHidden: true,
  });

  // A second subscription to the same query, adding a refetch policy rather
  // than a second request — the shape `BoardPage` and Overview established.
  // Conditional on something actually being in flight (spec 110 FR-018):
  // unconditional polling of a whole workspace is a constitution violation, and
  // a page at rest must cost nothing. Background tabs never poll
  // (`refetchIntervalInBackground` is off app-wide).
  // Held once: it decides the polling and it is what the header reports
  // (spec 113 FR-017), so the two can never disagree.
  const live = hasNonTerminalActivity(workspace?.tasks ?? []);
  const refresh = useWorkspace(workspaceId, {
    refetchInterval: live ? LIVE_POLL_MS : false,
    // An idle page reads again when the reader comes back (spec 126 R1).
    refetchOnWindowFocus: 'always',
    // Must match the read above, or the poll would refresh a cache entry
    // nothing on this page is reading (the board's rule).
    includeHidden: view.hidden,
  });

  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(isPending);

  // Two lines or one (spec 114 US2) — a device preference, read once here and
  // handed to everything that draws a row, so none of them can disagree.
  const density = usePreferencesStore((state) => state.taskListDensity);

  // Measured at the moment the workspace was read (see `useTaskListRows`).
  const {
    allRows,
    rows: readRows,
    resolved,
  } = useTaskListRows(workspace, view, dataUpdatedAt);
  // Rows a refresh brought arrive in place (spec 148), held back only while
  // the pointer or focus is in the list or a task's menu is open, so no row
  // moves under the person. The view's own filters already applied, so a task
  // they hide never shows. A new view — any change to the address's filters —
  // is a new start.
  const inList = useInteractionHold(
    (state) =>
      state.menusOpen > 0 ||
      state.pointerZone === LIST_ZONE ||
      state.focusZone === LIST_ZONE,
  );
  const isHolding = useCallback(() => inList, [inList]);
  const arrivals = useArrivalHold(
    readRows,
    JSON.stringify(view),
    rowTaskId,
    isHolding,
  );
  const rows = arrivals.visible;

  /*
    Hidden only for a workspace with no tasks at all — a search box above
    nothing invites typing into a page that could never answer. Shown while the
    aggregate is still loading, because that is what stops the reserved rows
    below from jumping when it lands, and shown when the filters have emptied
    the list, because that is exactly when someone needs to reach the control
    that put them there.
  */
  const showFilters = isPending || (workspace?.tasks.length ?? 0) > 0;
  const isOrgAdmin = useIsOrgAdmin();
  // The whole workspace's line, for the capacity meter's popover (spec 118).
  const queue = useWorkspaceQueue(workspace);

  return (
    // The workspace's limit, for the rows' and the drawer's queue sentences (spec 115).
    <WorkspaceConcurrencyProvider limit={workspace?.concurrency?.limit}>
      <section className="flex min-h-0 flex-1 flex-col gap-5 p-6 max-md:min-h-max max-md:px-4 max-md:pt-4 pb-12">
        {/*
        The filter row sits directly under the header, in the same slot the
        board and Overview use. Each of the three used to arrange it
        differently, and this was the only one of them that could search at all.

        Rendered whether or not the aggregate has landed: it needs only the
        label registry, which is empty until then and harmless while it is, and
        rendering it late was what made every row jump downward the moment data
        arrived. `/` belongs to the search box here and nowhere else — this is
        the page whose primary act is searching.
      */}
        <PageHeader
          surface="inverse"
          eyebrow="Workspace"
          title="Tasks"
          // Only once the workspace is read: before that, "Up to date" would be
          // a claim about data the page does not have yet.
          aside={
            workspace ? (
              <LiveStatus
                live={live}
                updatedAt={refresh.dataUpdatedAt}
                failed={refresh.isRefetchError}
                retrying={refresh.isFetching}
                onRetry={() => {
                  void refresh.refetch();
                }}
              />
            ) : undefined
          }
          // How busy the workspace is, always (spec 118): it replaced the queue
          // notice, as on the board.
          actions={
            // On a phone the meter and New task share the line evenly, as
            // Select and New task do on the board (spec 271).
            <div className="flex flex-wrap items-center gap-2 max-md:w-full max-md:flex-nowrap max-md:[&>*]:flex-1 max-md:[&>*]:justify-center">
              <CapacityMeter
                capacity={workspace?.concurrency}
                workspaceId={workspaceId}
                queue={queue}
                isAdmin={isOrgAdmin}
              />
              {workspace ? (
                <NewTaskMenu
                  workspaceId={workspaceId}
                  workflows={workspace.workflows}
                />
              ) : null}
            </div>
          }
        />

        {/* Its own line, as on the board and Overview. */}
        {showFilters ? (
          <TaskListFilters
            registry={workspace?.labels ?? []}
            workflows={workspace?.workflows ?? []}
            tasks={workspace?.tasks ?? []}
            view={view}
            onViewChange={onViewChange}
            // `/` belongs to the search box — except while a task's drawer or the
            // Filters panel covers the page, when it would reach through to a
            // field behind it (spec 111 FR-006, spec 113).
            slashFocusesSearch={!drawerOpen && !sheetOpen}
            // How many, and out of how many — at the end of the row whose
            // filters produced it (spec 113 FR-013). Both figures come from the
            // derivation the list is drawn from, so the two cannot disagree.
            shown={rows.length}
            total={allRows.length}
            sheetOpen={sheetOpen}
            onSheetOpenChange={setSheetOpen}
          />
        ) : null}

        {/* What the list is narrowed to, at every width (spec 113 US1). */}
        {workspace ? (
          <TaskListChips
            view={view}
            onViewChange={onViewChange}
            registry={workspace.labels ?? []}
            workflows={workspace.workflows}
            tasks={workspace.tasks}
          />
        ) : null}

        <FeatureErrorBoundary fallbackMessage="This page could not be shown.">
          {isPending ? (
            showPlaceholder && (
              <>
                <TaskTilesSkeleton />
                <SkeletonRows rowHeight={taskRowHeight(density)} />
              </>
            )
          ) : !workspace ? (
            <ReadFailure
              message="This workspace could not be read, so nothing on this page can be shown."
              onRetry={() => {
                void refresh.refetch();
              }}
              isFetching={refresh.isFetching}
            />
          ) : (
            <TaskList
              workspaceId={workspaceId}
              workspace={workspace}
              view={view}
              allRows={allRows}
              rows={rows}
              resolved={resolved}
              density={density}
              onViewChange={onViewChange}
              keyboardSuspended={drawerOpen}
            />
          )}
        </FeatureErrorBoundary>

        {/*
        Outside the list, so a drawer named by the address opens whatever the
        list shows — narrowed away from its task, or empty.
      */}
        {workspace && openTaskId ? (
          <TaskDrawerHost
            key={openTaskId}
            workspaceId={workspaceId}
            workspace={workspace}
            taskId={openTaskId}
            tab={openTab}
            view={view}
            onViewChange={onViewChange}
            onTabChange={onTabChange}
            onClose={onCloseTask}
            previewPath={previewPath}
            onPreviewClose={onPreviewClose}
          />
        ) : null}
      </section>
    </WorkspaceConcurrencyProvider>
  );
}

/**
 * The task drawer, shown over the Tasks page instead of on the task's board
 * (spec 111).
 *
 * The drawer reads no router state; everything the board would hand it comes
 * from `taskPlacement` over the aggregate this page already holds, so opening a
 * task makes no request (FR-007). Keyed by task in the caller, as on the board,
 * so one task's unsaved description can never be carried into another's.
 */
function TaskDrawerHost({
  workspaceId,
  workspace,
  taskId,
  tab,
  view,
  onViewChange,
  onTabChange,
  onClose,
  previewPath,
  onPreviewClose,
}: {
  workspaceId: string;
  workspace: NonNullable<ReturnType<typeof useWorkspace>['data']>;
  taskId: string;
  tab: TaskTab | undefined;
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
  onTabChange: (tab: TaskTab, options?: { replace?: boolean }) => void;
  onClose: (options?: { replace?: boolean }) => void;
  previewPath: string | undefined;
  onPreviewClose: () => void;
}) {
  const placement = useMemo(
    () => taskPlacement(workspace.workflows, workspace.tasks, taskId),
    [workspace.workflows, workspace.tasks, taskId],
  );

  /*
    Hiding a task from its drawer closes the drawer (FR-014). This page does not
    list hidden tasks, so once the service hides it the next read no longer
    carries it — and a drawer left open would only say it cannot be read.

    Keyed on the task having been *seen* since this drawer opened, so an
    address naming a task the workspace never had keeps its drawer and its
    "could not be read" answer instead of closing before anyone reads it.
  */
  const relatedHost = useMemo(
    () => tasksPageHost(searchOfListView(view)),
    [view],
  );

  const seen = useRef(false);
  const present = placement.task !== undefined;
  useEffect(() => {
    if (present) {
      seen.current = true;
    } else if (seen.current) {
      onClose({ replace: true });
    }
  }, [present, onClose]);

  return (
    // Related tasks open here too, not on their boards (FR-009, FR-010).
    <RelatedTaskHostProvider value={relatedHost}>
      <TaskDrawer
        taskId={taskId}
        workspaceId={workspaceId}
        workflowKey={placement.workflow?.key}
        task={placement.task}
        location={placement.location}
        stepLabels={placement.stepLabels}
        steps={placement.steps}
        labelRegistry={workspace.labels ?? []}
        onNarrowToLabel={(labelId) => {
          // The label joins the list's view, and the drawer closes —
          // view rebuilds the address without `task` — so the answer is
          // shown rather than hidden behind the question (as on the board).
          const current = view.labels?.kind === 'labels' ? view.labels.ids : [];
          onViewChange({
            ...view,
            labels: {
              kind: 'labels',
              ids: current.includes(labelId) ? current : [...current, labelId],
            },
          });
        }}
        tab={tab}
        onTabChange={onTabChange}
        onClose={() => {
          onClose();
        }}
        previewPath={previewPath}
        onPreviewClose={onPreviewClose}
        openOnBoard={
          // A draft has no board to open (FR-013).
          placement.workflow?.status === 'published'
            ? { workflowKey: placement.workflow.key }
            : undefined
        }
        finalFocus={() =>
          // Back to the row this task was opened from, or the list when that
          // row is gone — the drawer can open from the address, where there is
          // no trigger for the sheet to return to (FR-005).
          document.querySelector<HTMLElement>(
            `[data-task-id="${CSS.escape(taskId)}"]`,
          ) ??
          document.querySelector<HTMLElement>('[data-task-list]') ??
          true
        }
      />
    </RelatedTaskHostProvider>
  );
}

function TaskList({
  workspaceId,
  workspace,
  view,
  allRows,
  rows,
  resolved,
  density,
  onViewChange,
  keyboardSuspended,
}: {
  workspaceId: string;
  workspace: NonNullable<ReturnType<typeof useWorkspace>['data']>;
  view: TaskListView;
  /** From `useTaskListRows`, derived once for the whole page (spec 113 R2). */
  allRows: TaskListRows['allRows'];
  rows: TaskListRows['rows'];
  resolved: TaskListRows['resolved'];
  density: TaskListDensity;
  onViewChange: (next: TaskListView) => void;
  /** A task's drawer covers the list; `j`/`k` must not reach behind it. */
  keyboardSuspended: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Pointer or focus in the list holds arrivals (see `WorkspaceTasksPage`).
  const holdZone = useHoldZone(LIST_ZONE);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual's returned functions can't be safely memoized by the React Compiler; this is expected.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => taskRowHeight(density),
    overscan: 8,
  });

  // The rows are positioned from measured offsets, so a change of height must
  // re-measure, or the list would keep the old spacing until scrolled.
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, density]);

  // `j` / `k` move through the list (spec 081). Scoped here rather than bound globally:
  // "next row" means nothing away from the rows, and a global letter would fight every
  // other surface for it.
  const isPaletteOpen = useCommandPaletteStore((state) => state.open);
  const listNav = useListNavigation({
    count: rows.length,
    scrollToIndex: (i) => {
      virtualizer.scrollToIndex(i);
    },
    suspended: isPaletteOpen || keyboardSuspended,
    containerRef: scrollRef,
  });

  // Over `allRows`, never `rows`: the tiles answer for the whole workspace
  // whatever the list below has been narrowed to (spec 110 FR-002). `Date.now()`
  // is read per render on purpose — "the last seven days" moves with the clock,
  // and a render is at most one poll old while anything is in flight.
  // Hidden tasks are never counted (spec 112 FR-019), even while listed.
  const tiles = useMemo(
    () =>
      summarizeTaskTiles(
        allRows.filter((row) => !row.hidden),
        Date.now(),
      ),
    [allRows],
  );
  const tileRow = (
    <TaskTiles
      tiles={tiles}
      capacity={workspace.concurrency}
      view={view}
      onViewChange={onViewChange}
    />
  );

  // The column model over the rows already narrowed and ordered above. It
  // sorts and filters nothing (spec 110 FR-017, research R5).
  // Over `allRows`, like the tiles: a search must not change what a workflow's
  // tasks usually take (spec 113 research R5). Hidden rows are excluded inside.
  const leadMedians = useMemo(
    () => workflowLeadMedians(allRows, Date.now()),
    [allRows],
  );

  const table = useTaskTable(rows, {
    workspaceId,
    leadMedians,
    density,
    onToggleLabel: (labelId) => {
      onViewChange(toggleLabel(view, labelId));
    },
  });

  const tableRows = table.getRowModel().rows;
  // Which rows were here at first paint (they land a beat apart) and which
  // arrived since (they fade in) — spec 122 FR-023, FR-024.
  const initialRows = useInitialIds(tableRows);
  const newRows = useNewIds(tableRows);
  const canWork = useCanWork();
  const activeFilters = describeFilters(
    resolved,
    workspace.labels ?? [],
    canWork,
  );
  const hasAnyTask = allRows.length > 0;

  if (rows.length === 0) {
    return (
      <>
        {tileRow}
        <TaskListEmptyState
          workspaceId={workspaceId}
          // An empty workspace and an over-narrowed one are different facts. The
          // filters are only named when they are what emptied the list.
          activeFilters={hasAnyTask ? activeFilters : []}
          onClearFilters={() => {
            onViewChange(clearFilters(view));
          }}
        />
      </>
    );
  }

  return (
    <>
      {tileRow}
      <div
        ref={scrollRef}
        data-task-list
        tabIndex={-1}
        {...holdZone}
        // On a phone the page scrolls and the list is a screen tall within it:
        // squeezed under the header and tiles, it scrolled in its last 250px
        // (spec 271).
        className="border-border min-h-0 flex-1 overflow-auto rounded-lg border focus-visible:outline-none max-md:h-[calc(100dvh-4rem)] max-md:flex-none"
      >
        {/*
          Inside the scroll container, so it sticks to the top of the list
          rather than the page. The virtual rows are positioned inside their own
          box beneath it, so no row offset has to account for it.
        */}
        <TaskListHeader
          table={table}
          sort={view.sort}
          onSortChange={(sort) => {
            onViewChange({ ...view, sort });
          }}
        />
        <div
          role="presentation"
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = tableRows[virtualRow.index];
            return (
              <div
                // Keyed by task, not by index: a poll-driven re-render reorders
                // this list, and an index key would recycle one task's row into
                // another task's place.
                key={row.id}
                // The index the keyboard navigates by, which is positional and so
                // deliberately not the key above.
                {...listNav.rowIndexAttribute(virtualRow.index)}
                className={`group/row absolute top-0 left-0 w-full ${
                  newRows.has(row.id)
                    ? ARRIVAL_FADE_CLASS
                    : initialRows.has(row.id) && virtualRow.index < 8
                      ? STAGGER_CLASS
                      : ''
                }`}
                style={{
                  transform: `translateY(${String(virtualRow.start)}px)`,
                  ...(initialRows.has(row.id)
                    ? staggerStyle(virtualRow.index)
                    : undefined),
                }}
              >
                <TaskRow
                  workspaceId={workspaceId}
                  row={row}
                  height={taskRowHeight(density)}
                />
                {/*
                  Beside the link, never inside it (spec 114 FR-011), so Tab
                  reaches them after their row. Shown on hover or focus — they
                  cover Cost and Updated while shown — except on a hidden row,
                  where Unhide is the point and stays (FR-014). Not on a narrow
                  window, where the drawer carries them (FR-015).
                */}
                <div
                  data-row-actions
                  data-always-shown={row.original.hidden ? 'true' : undefined}
                  className={`bg-accent absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-0.5 rounded-md transition-opacity max-md:hidden ${
                    row.original.hidden
                      ? ''
                      : 'opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100'
                  }`}
                >
                  <TaskRowActions
                    workspaceId={workspaceId}
                    row={row.original}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/**
 * The active filters in words, for an empty result that has to name what
 * excluded things rather than merely reporting that nothing matched.
 */
function describeFilters(
  view: TaskListView,
  registry: readonly { id: string; name: string }[],
  canWork: boolean,
): string[] {
  const parts: string[] = [];
  if (view.q) parts.push(`“${view.q}”`);
  if (view.labels?.kind === 'unlabelled') parts.push('unlabelled');
  if (view.labels?.kind === 'labels') {
    const named = view.labels.ids
      .map((id) => registry.find((l) => l.id === id)?.name)
      .filter((n): n is string => n !== undefined);
    if (named.length > 0) parts.push(named.join(' + '));
  }
  if (view.states.length > 0) {
    const word = canWork ? STATE_WORD : READER_STATE_WORD;
    parts.push(view.states.map((state) => word[state]).join(' or '));
  }
  if (view.workflows.length > 0) parts.push(view.workflows.join(' or '));
  if (view.assignee) {
    parts.push(
      view.assignee === 'me'
        ? 'assigned to you'
        : view.assignee === 'none'
          ? 'unassigned'
          : 'one assignee',
    );
  }
  if (view.creator) {
    parts.push(view.creator === 'me' ? 'created by you' : 'one creator');
  }
  if (view.range)
    parts.push(`${view.range.on} in the last ${view.range.since}`);
  return parts;
}

const STATE_WORD: Record<TaskListView['states'][number], string> = {
  running: 'in progress',
  queued: 'queued',
  subtasks: 'waiting on subtasks',
  waiting: 'waiting on you',
  failed: 'errors',
  blocked: 'spend limit',
  review: 'needs review',
  attention: 'needs attention',
  'to-start': 'subtasks to start',
  'at-rest': 'at rest',
  completed: 'completed',
};

/** A reader cannot answer a gate, so it waits on a member (spec 193). */
const READER_STATE_WORD: typeof STATE_WORD = {
  ...STATE_WORD,
  waiting: READER_WAITING_LABEL.toLowerCase(),
};

/**
 * Add or remove one label from the view.
 *
 * Toggling off the last one drops the member entirely rather than leaving an
 * empty list, so an unfiltered page says so by carrying nothing — the rule the
 * address already follows everywhere else.
 */
function toggleLabel(view: TaskListView, labelId: string): TaskListView {
  const current = view.labels?.kind === 'labels' ? view.labels.ids : [];
  const next = current.includes(labelId)
    ? current.filter((id) => id !== labelId)
    : [...current, labelId];

  return {
    ...view,
    labels: next.length > 0 ? { kind: 'labels', ids: next } : undefined,
  };
}
